import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Spin, message } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from '../../../i18n';
import { getCollaborationDetail } from '../../../store/request';
import type {
  TargetCollaborationDetail,
  TargetCollaborationOpLog
} from '../../../types/collaboration';

type InviteDetailModalProps = {
  open: boolean;
  collaborationId?: number;
  shopId?: string | null;
  onClose: () => void;
};

type CreateRequestSummary = {
  name?: string;
  endTime?: string;
  productIds?: string[];
  creatorOpenIds?: string[];
};

function formatCount(value?: number) {
  if (value == null || !Number.isFinite(value)) return '—';
  return String(value);
}

function formatTime(value?: string) {
  if (!value) return '—';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return parsed.format('YYYY-MM-DD HH:mm');
}

/** 平台佣金多为基点：1500 = 15%。小于等于 100 时按百分数原样展示。 */
function formatCommissionRate(rate?: number | null) {
  if (rate == null || !Number.isFinite(rate)) return '—';
  if (Math.abs(rate) > 100) {
    const percent = rate / 100;
    return `${Number.isInteger(percent) ? percent : percent.toFixed(2)}%`;
  }
  return `${rate}%`;
}

function parseCreateSummary(operations: TargetCollaborationOpLog[]): CreateRequestSummary | null {
  const createOp = operations.find((item) => item.operationType?.trim().toUpperCase() === 'CREATE');
  const raw = createOp?.requestSummary?.trim();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CreateRequestSummary;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 text-xs font-medium tracking-wide text-slate-500">{children}</div>;
}

function MetaField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-xs leading-relaxed text-slate-400">{label}</div>
      <div className="mt-1 break-words text-sm leading-relaxed text-slate-800">{children}</div>
    </div>
  );
}

function StatBlock({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <div className="invite-detail-stat-item text-center sm:text-left">
      <div className="text-lg font-semibold tabular-nums text-slate-800 sm:text-xl">{value}</div>
      <div className="mt-0.5 text-xs text-slate-400">{label}</div>
    </div>
  );
}

export function InviteDetailModal({ open, collaborationId, shopId, onClose }: InviteDetailModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<TargetCollaborationDetail | null>(null);
  const currentShopId = shopId?.trim() || '';

  useEffect(() => {
    if (!open) {
      setDetail(null);
      setLoading(false);
      return;
    }
    if (!currentShopId || collaborationId == null) {
      setDetail(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    getCollaborationDetail(collaborationId, { shopId: currentShopId })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('invitation.detail_load_failed'));
          setDetail(null);
          return;
        }
        setDetail(res.content ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('invitation.detail_load_failed'));
          setDetail(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, collaborationId, currentShopId, t]);

  const collaboration = detail?.collaboration ?? null;
  const productRecords = detail?.products;
  const creatorRecords = detail?.creators;
  const operationRecords = detail?.operations;
  const products = Array.isArray(productRecords) ? productRecords : [];
  const creators = Array.isArray(creatorRecords) ? creatorRecords : [];
  const operations = Array.isArray(operationRecords) ? operationRecords : [];
  const createSummary = useMemo(() => parseCreateSummary(operations), [operations]);

  const inviteName = collaboration?.name?.trim() || createSummary?.name?.trim() || '';
  const endTime = collaboration?.endTime || createSummary?.endTime;
  const shopIdText = collaboration?.shopId?.trim() || currentShopId || '—';
  const invitedCount = collaboration?.invitedCreatorCount ?? creators.length;
  const acceptedCount = collaboration?.acceptedCreatorCount;
  const fulfilledCount = collaboration?.fulfilledCreatorCount;
  const freeSampleText =
    detail?.hasFreeSample == null
      ? '—'
      : detail.hasFreeSample
        ? t('invitation.detail_yes')
        : t('invitation.detail_no');
  const sampleExemptText =
    detail?.sampleApprovalExempt == null
      ? ''
      : ` · ${t('invitation.detail_sample_exempt')} ${detail.sampleApprovalExempt ? t('invitation.detail_yes') : t('invitation.detail_no')
      }`;
  const failedMsg = detail?.failedMsg?.trim() || '';

  return (
    <Modal
      className="invite-detail-modal"
      title={t('invitation.detail_title')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={680}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      footer={
        <div className="flex justify-end gap-2">
          <Button className="rounded-lg" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-10">
          <Spin />
        </div>
      ) : !detail ? (
        <p className="mb-0 py-6 text-center text-sm text-slate-400">{t('invitation.detail_empty')}</p>
      ) : (
        <div className="space-y-5">
          {detail.syncPending ? (
            <p className="mb-0 text-xs leading-relaxed text-amber-600">{t('invitation.detail_sync_pending')}</p>
          ) : null}

          <div className="rounded-xl border border-[#eef0f4] bg-white px-4 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="break-words text-sm font-semibold leading-snug text-slate-900">
              {inviteName || '—'}
            </div>
            <div className="mt-3 whitespace-pre-wrap break-words rounded-lg bg-[#f4f6fa] px-3 py-2.5 text-sm leading-relaxed text-slate-500">
              {detail.message?.trim() || '—'}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-4">
              <MetaField label={t('invitation.contact_email')}>
                <span className="break-all">{detail.contactEmail?.trim() || '—'}</span>
              </MetaField>
              <MetaField label={t('invitation.detail_deadline')}>{formatTime(endTime)}</MetaField>
              <MetaField label={t('invitation.detail_shop_id')}>
                <span className="break-all">{shopIdText}</span>
              </MetaField>
              <MetaField label={t('invitation.detail_free_sample')}>
                {freeSampleText}
                {sampleExemptText}
              </MetaField>
              {detail.preferredContentType?.trim() ? (
                <MetaField label={t('invitation.detail_content_type')}>
                  {detail.preferredContentType.trim()}
                </MetaField>
              ) : null}
            </div>
            {failedMsg ? (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#ffccc7] bg-[#fff2f0] px-3 py-2.5 text-sm leading-relaxed text-[#cf1322]">
                <ExclamationCircleFilled className="mt-0.5 shrink-0 text-base" />
                <span className="min-w-0 whitespace-pre-wrap break-words">
                  {t('invitation.detail_submit_failed', { reason: failedMsg })}
                </span>
              </div>
            ) : null}
          </div>

          <div>
            <SectionTitle>
              {t('invitation.mounted_products')}
              <span className="ml-1 tabular-nums text-slate-400">({products.length})</span>
            </SectionTitle>
            {products.length === 0 ? (
              <p className="mb-0 rounded-xl border border-dashed border-[#e8ebf0] px-3 py-5 text-center text-sm text-slate-400">
                {t('invitation.detail_empty_products')}
              </p>
            ) : (
              <div className="overflow-hidden rounded-xl border border-[#eef0f4]">
                {products.map((item, index) => {
                  const productId = item.productId?.trim() || '';
                  const name = item.productName?.trim() || productId || '—';
                  return (
                    <div
                      key={productId || `product-${index}`}
                      className="flex flex-wrap items-center gap-3 border-b border-[#eef0f4] px-3 py-3 last:border-b-0 sm:flex-nowrap"
                    >
                      {item.mainImageUrl?.trim() ? (
                        <img
                          src={item.mainImageUrl}
                          alt=""
                          className="size-[56px] shrink-0 rounded-lg object-cover"
                        />
                      ) : (
                        <span className="inline-flex size-[56px] shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[11px] font-semibold tracking-wide text-slate-400">
                          SKU
                        </span>
                      )}
                      <div className="min-w-0 w-[340px] shrink break-words text-[13px] font-semibold leading-snug text-slate-800">
                        {name}
                      </div>
                      <div className="ml-auto flex shrink-0 items-center gap-2 pl-2 sm:pl-0">
                        <span className="text-xs text-slate-500">{t('invitation.target_commission')}</span>
                        <span className="text-sm font-semibold tabular-nums text-slate-800">
                          {formatCommissionRate(item.targetCommissionRate)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <SectionTitle>
              {t('invitation.talents')}
              <span className="ml-1 tabular-nums text-slate-400">({creators.length})</span>
            </SectionTitle>
            <div className="invite-detail-stats mb-3 grid grid-cols-3">
              <StatBlock value={formatCount(invitedCount)} label={t('invitation.col_added')} />
              <StatBlock value={formatCount(acceptedCount)} label={t('invitation.col_accepted')} />
              <StatBlock value={formatCount(fulfilledCount)} label={t('invitation.col_fulfilled')} />
            </div>
            {creators.length === 0 ? (
              <p className="mb-0 rounded-xl border border-dashed border-[#e8ebf0] px-3 py-5 text-center text-sm text-slate-400">
                {t('invitation.detail_empty_talents')}
              </p>
            ) : (
              <div className="overflow-hidden rounded-xl border border-[#eef0f4]">
                {creators.map((creator, index) => {
                  const name =
                    creator.nickname?.trim() ||
                    creator.creatorName?.trim() ||
                    creator.platformNickname?.trim() ||
                    creator.username?.trim() ||
                    creator.platformUsername?.trim() ||
                    '—';
                  const handle = (creator.username?.trim() || creator.platformUsername?.trim() || '').replace(/^@/, '');
                  const influencerId = creator.influencerId?.trim() || '';
                  const avatarUrl = creator.avatarUrl?.trim() || '';
                  return (
                    <div
                      key={influencerId || `creator-${index}`}
                      className="flex items-center gap-3 border-b border-[#eef0f4] px-3 py-3 last:border-b-0"
                    >
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="" className="size-10 shrink-0 rounded-full object-cover" />
                      ) : (
                        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-[#596ae8] text-sm font-semibold text-white">
                          {name === '—' ? '?' : name.slice(0, 1).toUpperCase()}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-slate-800">{name}</div>
                        {handle || influencerId ? (
                          <div className="mt-0.5 truncate text-xs text-slate-400">
                            {handle ? `@${handle}` : influencerId}
                          </div>
                        ) : null}
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-sm font-semibold tabular-nums text-slate-800">
                          {formatCount(creator.contentProductCount)}
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-400">
                          {t('invitation.detail_content_products')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
